// src/hooks/useDashboardLayout.js
//
// Dashboard widget layout manager. Stores a per-user preferred order +
// enabled set in localStorage so users can hide widgets they don't care
// about and reorder the ones they do.
//
// The hook doesn't know about concrete widgets — it works against the
// caller-supplied `defaults` list of `{ id, label }`. The caller renders
// widgets in the order returned by `visible`.

import { useCallback, useEffect, useMemo, useState } from 'react';

const KEY = 'caro.portal.dashboard_layout';

const load = () => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const save = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
};

/**
 * @param {Array<{id:string,label:string,description?:string,enabled?:boolean}>} defaults
 *   Widget catalog. The order in this array is the fallback order when
 *   nothing is persisted; `enabled: false` lets a widget default to
 *   hidden.
 */
export const useDashboardLayout = (defaults) => {
  const build = useCallback(() => {
    const persisted = load() || {};
    const seen = new Set();
    const next = [];
    // Honor the user's persisted order first, but only for widgets that
    // still exist in `defaults`.
    for (const id of persisted.order || []) {
      const def = defaults.find((d) => d.id === id);
      if (!def) continue;
      seen.add(id);
      next.push({
        ...def,
        enabled: persisted.enabled?.[id] ?? (def.enabled !== false),
      });
    }
    // Append any new-from-defaults widgets the user hasn't seen yet.
    for (const def of defaults) {
      if (seen.has(def.id)) continue;
      next.push({ ...def, enabled: persisted.enabled?.[def.id] ?? (def.enabled !== false) });
    }
    return next;
  }, [defaults]);

  const [widgets, setWidgets] = useState(build);

  // Re-derive if the catalog changes (feature flag adds/removes a widget).
  useEffect(() => {
    setWidgets(build());
  }, [build]);

  const persist = useCallback((next) => {
    save({
      order: next.map((w) => w.id),
      enabled: Object.fromEntries(next.map((w) => [w.id, w.enabled])),
    });
  }, []);

  const toggle = useCallback((id) => {
    setWidgets((prev) => {
      const next = prev.map((w) => (w.id === id ? { ...w, enabled: !w.enabled } : w));
      persist(next);
      return next;
    });
  }, [persist]);

  const move = useCallback((id, delta) => {
    setWidgets((prev) => {
      const idx = prev.findIndex((w) => w.id === id);
      if (idx < 0) return prev;
      const target = idx + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(idx, 1);
      next.splice(target, 0, moved);
      persist(next);
      return next;
    });
  }, [persist]);

  const moveTo = useCallback((id, targetIndex) => {
    setWidgets((prev) => {
      const idx = prev.findIndex((w) => w.id === id);
      if (idx < 0) return prev;
      const clamped = Math.max(0, Math.min(prev.length - 1, targetIndex));
      if (clamped === idx) return prev;
      const next = [...prev];
      const [moved] = next.splice(idx, 1);
      next.splice(clamped, 0, moved);
      persist(next);
      return next;
    });
  }, [persist]);

  const reset = useCallback(() => {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    setWidgets(build());
  }, [build]);

  const visible = useMemo(() => widgets.filter((w) => w.enabled), [widgets]);

  return {
    widgets,
    visible,
    toggle,
    moveUp: (id) => move(id, -1),
    moveDown: (id) => move(id, 1),
    moveTo,
    reset,
  };
};

export default useDashboardLayout;
